import { useEffect, useState } from "react";
import { AppButton, AppDialog, FormField, FormSection } from "../../../../../components/ui";
import {
  ApplicationRole,
  ClientRepresentationRequest,
  CSAType,
  DeclinedAction,
  GetUserListRequest,
  LeadCommentRequest,
  LeadsService,
  LeadViewModel,
  SendLeadRequest,
  UserShortResponse,
  UsersService,
} from "../../../../../api";
import { AdminLeadStatus } from "../../../../../api/models/AdminLeadStatus";
import { HelpersService } from "../../../../../services/Helpers";
import { CSADisplayText, errorInstructions } from "../../shared/Constants";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import useNavigateSearch from "../../../../../services/useNavigateSearch";
import { useDispatch } from "react-redux";
import {
  startSpinner,
  stopSpinner,
} from "../../../../../redux/actions/spinnerActions";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { MenuItem, Stack } from "@mui/material";
import LeadSideRailPortal from "./LeadSideRailPortal";

const MySwal = withReactContent(Swal);

interface KeepActionsProps {
  details?: LeadViewModel;
  userRoles?: ApplicationRole[];
}

interface ConfirmCSAFields {
  firstName: string;
  lastName: string;
  company: string;
}

export default function KeepProcessActions(props: KeepActionsProps) {
  const [selectedCSA, setSelectedCSA] = useState<CSAType>();
  const [staffUsers, setStaffUsers] = useState<UserShortResponse[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<number>();
  const [btnPressed, setBtnPressed] = useState<boolean>();
  const [openSendCSADialog, setOpenSendCSADialog] = useState(false);
  const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false);
  const [confirmFields, setConfirmFields] = useState<ConfirmCSAFields>({
    firstName: "",
    lastName: "",
    company: "",
  });
  const [receiverEmail, setReceiverEmail] = useState<string>();
  const [emailMessage, setEmailMessage] = useState<string>();

  const [openDeclinedDialog, setOpenDeclinedlDialog] = useState(false);
  const [selectedDeclineAction, setSelectedDeclineAction] =
    useState<DeclinedAction>();

  const navigate = useNavigateSearch();
  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedCSA(
      HelpersService.getEnumKeyByEnumValue(CSAType, props.details?.csaType)
    );

    if (props.userRoles?.includes(ApplicationRole.ADMIN)) {
      UsersService.postApiUsersGetUsersByRole({
        role: ApplicationRole.STAFF,
      } as GetUserListRequest).then((users) => {
        setStaffUsers(users);
      });
    }

    if (props.details?.supplementalAssignedTo) {
      setSelectedStaff(props.details?.supplementalAssignedTo);
    }
    setConfirmFields({
      firstName: props.details?.firstName ?? "",
      lastName: props.details?.lastName ?? "",
      company: props.details?.employmentDetails?.company ?? "",
    });
  }, [props.details?.csaType]);

  const handleSendCSAClose = (success) => {
    if (success) {
      dispatch(startSpinner());
      var request = {
        success: true,
        csaType: CSAType[selectedCSA!],
        firstName: confirmFields.firstName,
        lastName: confirmFields.lastName,
        company: confirmFields.company,
      } as ClientRepresentationRequest;
      LeadsService.postApiLeadsCsaAction(props.details!.id!, request)
        .then(() => {
          MySwal.fire({
            icon: "success",
            title: <p className="title">CSA was sent successfully.</p>,
          }).then(() => window.location.reload());
        })
        .catch(() => {
          MySwal.fire({
            icon: "error",
            title: <p className="title">Error updating CSA.</p>,
            html: <div className="alert-text">{errorInstructions}</div>,
          });
        })
        .finally(() => dispatch(stopSpinner()));
    }
    setBtnPressed(false);
    setOpenSendCSADialog(false);
  };

  const handleSendEmailClose = (success) => {
    if (success) {
      handleSendEmail(receiverEmail, emailMessage, false);
      LeadsService.postApiLeadsLeaveComment(props.details?.id!, {
        message: "Lead data was shared",
        sendEmail: false,
        updateStatus: false,
      } as LeadCommentRequest).then(() =>
        MySwal.fire({
          icon: "success",
          title: <p className="title">Email was sent.</p>,
        }).then(() => window.location.reload())
      );
    }
    setOpenSendEmailDialog(false);
  };

  const handleSendCSA = () => {
    setBtnPressed(true);
    if (!selectedCSA) {
      MySwal.fire({
        icon: "error",
        title: <p className="title">Please select CSA type</p>,
      });
      return;
    }
    setOpenSendCSADialog(true);
    setBtnPressed(false);
  };

  const handleClientDenial = () => {
    MySwal.fire({
      title: <strong>Action confirmation</strong>,
      html: <div>Are you sure you want to decline this client?</div>,
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, confirm",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed && !!selectedDeclineAction) {
        dispatch(startSpinner());
        var request = {
          success: false,
          declinedAction: DeclinedAction[selectedDeclineAction],
        } as ClientRepresentationRequest;
        LeadsService.postApiLeadsCsaAction(props.details!.id!, request)
          .then(() => {
            MySwal.fire({
              icon: "success",
              title: <p className="title">Client declined successfully.</p>,
            }).then(() => navigate("/active-clients"));
          })
          .catch(() => {
            MySwal.fire({
              icon: "error",
              title: <p className="title">Error declining client.</p>,
              html: <div className="alert-text">{errorInstructions}</div>,
            });
          })
          .finally(() => dispatch(stopSpinner()));
      }
    });
  };

  const handleConfirmSign = () => {
    LeadsService.putApiLeadsConfirmCsaSigned(props.details!.id!).then(() => {
      MySwal.fire({
        icon: "success",
        title: <p className="title">CSA signing confirmed</p>,
      }).then(() => window.location.reload());
    });
  };

  const openSupplementalForm = () => {
    navigate(`/lead/${props.details?.id}/supplemental`);
  };

  const assignSupplementalForm = () => {
    LeadsService.postApiLeadsAssignStaffToSupplemental(
      props.details!.id!,
      selectedStaff
    )
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: (
            <p className="title">
              You've successfully assigned supplemental to a staff.
            </p>
          ),
          html: <div>You will be notified when it is filled.</div>,
        }).then(() => window.location.reload());
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error assigning supplemental.</p>,
          html: <div className="alert-text">{errorInstructions}</div>,
        });
      });
  };

  const handleSendEmail = (
    email: string | undefined,
    emailMessage: string | undefined,
    withSupplemental: boolean
  ) => {
    dispatch(startSpinner());
    LeadsService.putApiLeadsSendEmail(props.details!.id!, {
      receiverEmail: email,
      saveAsFile: !email,
      message: emailMessage,
      withSupplemental: withSupplemental,
    } as SendLeadRequest)
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: (
            <p className="title">Email with lead data was sent successfully</p>
          ),
          html: <div>A copy was sent to you.</div>,
        }).then();
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error declining client.</p>,
          html: <div className="alert-text">{errorInstructions}</div>,
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const isAdmin = props.userRoles?.includes(ApplicationRole.ADMIN);
  const isStaff = props.userRoles?.includes(ApplicationRole.STAFF);
  const status = props.details?.status;

  const showNewActions = isAdmin && status === AdminLeadStatus.NEW;
  const showContractSent =
    isAdmin && status === AdminLeadStatus.CONTRACT_SENT;
  const showSupplementalAssign =
    isAdmin &&
    (status === AdminLeadStatus.CONTRACT_SIGNED ||
      status === AdminLeadStatus.SUPPLEMENTAL_ASSIGNED);
  const showDoneActions = isAdmin && status === AdminLeadStatus.DONE;
  const showStaffFill =
    isStaff && status === AdminLeadStatus.SUPPLEMENTAL_ASSIGNED;

  const hasSideActions =
    showNewActions ||
    showContractSent ||
    showSupplementalAssign ||
    showDoneActions ||
    showStaffFill;

  return (
    <>
      {hasSideActions && (
        <LeadSideRailPortal>
          {showNewActions && (
            <FormSection
              className="lead-side-panel"
              title="CSA actions"
              subtitle="Send the agreement or decline the client."
              icon={<GavelOutlinedIcon />}
            >
              <Stack spacing={1.5}>
                <FormField
                  select
                  size="small"
                  fullWidth
                  label="CSA type"
                  value={selectedCSA ?? ""}
                  onChange={(e) => setSelectedCSA(e.target.value as CSAType)}
                >
                  {Object.keys(CSAType).map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {CSADisplayText[CSAType[reason]]}
                    </MenuItem>
                  ))}
                </FormField>
                <AppButton
                  disabled={btnPressed}
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSendCSA}
                >
                  Send CSA
                </AppButton>
                <AppButton
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  onClick={() => setOpenDeclinedlDialog(true)}
                >
                  Declined
                </AppButton>
                <AppButton
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<MailOutlineIcon />}
                  onClick={() => setOpenSendEmailDialog(true)}
                >
                  Send lead data as email
                </AppButton>
              </Stack>
            </FormSection>
          )}

          {showContractSent && (
            <FormSection
              className="lead-side-panel"
              title="Contract"
              subtitle="Confirm when the client has signed."
              icon={<DescriptionOutlinedIcon />}
            >
              <AppButton
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleConfirmSign}
              >
                Confirm contract signed
              </AppButton>
            </FormSection>
          )}

          {showSupplementalAssign && (
            <FormSection
              className="lead-side-panel"
              title="Supplemental"
              subtitle="Assign staff or open the supplemental form."
              icon={<AssignmentIndOutlinedIcon />}
            >
              <Stack spacing={1.5}>
                <FormField
                  select
                  size="small"
                  fullWidth
                  label="Staff for supplemental"
                  value={selectedStaff ?? ""}
                  onChange={(e) => setSelectedStaff(Number(e.target.value))}
                >
                  {staffUsers.map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.firstName} {staff.lastName}
                    </MenuItem>
                  ))}
                </FormField>
                <AppButton
                  disabled={!selectedStaff}
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={assignSupplementalForm}
                >
                  {props.details?.supplementalAssignedTo
                    ? "Reassign supplemental form"
                    : "Assign supplemental form"}
                </AppButton>
                <AppButton
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={openSupplementalForm}
                >
                  Fill supplemental form
                </AppButton>
              </Stack>
            </FormSection>
          )}

          {showDoneActions && (
            <FormSection
              className="lead-side-panel"
              title="Completed lead"
              subtitle="Share materials or reopen supplemental."
              icon={<DescriptionOutlinedIcon />}
            >
              <Stack spacing={1.5}>
                <AppButton
                  disabled={btnPressed}
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => handleSendEmail(undefined, undefined, true)}
                >
                  Send
                </AppButton>
                <AppButton
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={openSupplementalForm}
                >
                  Open supplemental form
                </AppButton>
              </Stack>
            </FormSection>
          )}

          {showStaffFill && (
            <FormSection
              className="lead-side-panel"
              title="Supplemental"
              subtitle="Complete the assigned supplemental form."
              icon={<AssignmentIndOutlinedIcon />}
            >
              <AppButton
                variant="contained"
                color="primary"
                fullWidth
                onClick={openSupplementalForm}
              >
                Fill supplemental form
              </AppButton>
            </FormSection>
          )}
        </LeadSideRailPortal>
      )}

      <AppDialog
        open={openSendCSADialog}
        onClose={() => handleSendCSAClose(false)}
        title="Please confirm company and lead names."
        actions={
          <>
            <AppButton
              color="gray"
              variant="contained"
              onClick={() => handleSendCSAClose(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!selectedCSA}
              color="primary"
              variant="contained"
              onClick={() => handleSendCSAClose(true)}
            >
              Send CSA
            </AppButton>
          </>
        }
      >
        <div className="row main-holder">
          <div className="col-12 mt-3">
            <FormField
              required
              label="Company name"
              fullWidth
              value={confirmFields.company}
              onChange={(event) =>
                setConfirmFields({
                  ...confirmFields,
                  company: event.target.value,
                })
              }
            />
          </div>
          <div className="col-6 mt-3">
            <FormField
              required
              label="First name"
              fullWidth
              value={confirmFields.firstName}
              onChange={(event) =>
                setConfirmFields({
                  ...confirmFields,
                  firstName: event.target.value,
                })
              }
            />
          </div>
          <div className="col-6 mt-3">
            <FormField
              required
              label="Last name"
              fullWidth
              value={confirmFields.lastName}
              onChange={(event) =>
                setConfirmFields({
                  ...confirmFields,
                  lastName: event.target.value,
                })
              }
            />
          </div>
        </div>
      </AppDialog>

      <AppDialog
        open={openSendEmailDialog}
        onClose={() => handleSendEmailClose(false)}
        title="Send lead data as email"
        actions={
          <>
            <AppButton
              color="gray"
              variant="contained"
              onClick={() => handleSendEmailClose(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!receiverEmail}
              color="primary"
              variant="contained"
              onClick={() => handleSendEmailClose(true)}
            >
              Send
            </AppButton>
          </>
        }
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormField
            required
            fullWidth
            name="email"
            label="Receiver email"
            value={receiverEmail}
            onChange={(e) => setReceiverEmail(e.target.value.trim())}
          />
          <FormField
            label="Email message"
            fullWidth
            multiline
            minRows={3}
            value={emailMessage}
            onChange={(event) => setEmailMessage(event.target.value)}
          />
        </Stack>
      </AppDialog>

      <AppDialog
        open={openDeclinedDialog}
        onClose={() => setOpenDeclinedlDialog(false)}
        title="Select reason."
        actions={
          <>
            <AppButton
              color="gray"
              variant="contained"
              onClick={() => setOpenDeclinedlDialog(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!selectedDeclineAction}
              color="primary"
              variant="contained"
              onClick={() => handleClientDenial()}
            >
              Confirm Decline
            </AppButton>
          </>
        }
      >
        <FormField
          select
          fullWidth
          label="Select reason"
          value={selectedDeclineAction ?? ""}
          onChange={(e) =>
            setSelectedDeclineAction(e.target.value as DeclinedAction)
          }
        >
          {Object.keys(DeclinedAction).map((reason) => (
            <MenuItem key={reason} value={reason}>
              {HelpersService.camelToString(DeclinedAction[reason])}
            </MenuItem>
          ))}
        </FormField>
      </AppDialog>
    </>
  );
}
